import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { TemplateItemsEditor } from "./TemplateItemsEditor";

export function TemplateEditor({
	initialName = "",
	initialItems = [""],
	submitLabel,
	onSubmit,
	onCancel,
}: {
	initialName?: string;
	initialItems?: string[];
	submitLabel: string;
	onSubmit: (name: string, items: { title: string }[]) => Promise<void>;
	onCancel?: () => void;
}) {
	const [name, setName] = useState(initialName);
	const [items, setItems] = useState<string[]>(
		initialItems.length ? initialItems : [""],
	);
	const [saving, setSaving] = useState(false);

	async function handleSubmit() {
		const trimmedName = name.trim();
		const cleanItems = items
			.map((it) => it.trim())
			.filter(Boolean)
			.map((title) => ({ title }));

		if (!trimmedName) {
			toast.error("Template name is required");
			return;
		}
		if (cleanItems.length === 0) {
			toast.error("Add at least one item");
			return;
		}

		setSaving(true);
		try {
			await onSubmit(trimmedName, cleanItems);
		} catch (e) {
			toast.error("Save failed", { description: (e as Error).message });
		} finally {
			setSaving(false);
		}
	}

	return (
		<div className="space-y-3">
			<div>
				<label className="block text-xs font-medium text-muted mb-1">
					Template name
				</label>
				<Input
					value={name}
					disabled={saving}
					placeholder="e.g. Release checklist"
					onChange={(e) => setName(e.target.value)}
					className="h-9"
				/>
			</div>

			<div>
				<label className="block text-xs font-medium text-muted mb-1.5">
					Items
				</label>
				<TemplateItemsEditor
					items={items}
					onChange={setItems}
					disabled={saving}
				/>
			</div>

			<div className="flex items-center gap-2 pt-1">
				<Button size="sm" onClick={handleSubmit} disabled={saving}>
					{saving && (
						<Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
					)}
					{submitLabel}
				</Button>
				{onCancel && (
					<Button
						variant="outline"
						size="sm"
						onClick={onCancel}
						disabled={saving}
					>
						Cancel
					</Button>
				)}
			</div>
		</div>
	);
}
